import { breakpoints } from '@constants'
import { useFeatureFlag } from '@hooks'
import KingdomBankLogo from '@images/kingdom-bank-icon-black.svg?react'
import OnboardingBanner from '@onboarding/views/OnboardingBanner'
import cn from 'classnames'
import { paths, useAuth, useGetUnreadTicketsQuery, useMyAccountAccessQry } from 'mmfintech-backend-api'
import { tr, useMatchMedia } from 'mmfintech-commons'
import { MMButton } from 'mmfintech-portal-commons'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { NotificationsBell } from '../NotificationsPanel'
import { CustomHeaderTitles } from './CustomHeaderTitles'
import {
  NAVIGATION_ROW_HEIGHT,
  pathsToHidePaymentButtons,
  pathsToIncreaseHeight,
  pathsToShowBottomBorder,
  pathsToShowOnlyMobilePaymentButtons
} from './constants'
import { HamburgerMenu } from './HamburgerMenu'
import { PaymentButtons } from './PaymentButtons'
import { PaymentButtonsMobile } from './PaymentButtonsMobile'
import { ProfileMenu } from './ProfileMenu'

import './header.scss'
import { MMLanguageMenu } from '@components/WebComponents'

export const Header = () => {
  const hasTransactionApprovalFlow = useFeatureFlag('transactionApprovalFlow')

  const [opened, setOpened] = useState<boolean>(false)
  const [visible, setVisible] = useState<boolean>(false)

  const location = useLocation()
  const pathName = useMemo(() => location.pathname, [location])
  const pathParam = useMemo(() => {
    const parts = location?.pathname?.split('/') // Split the string by '/'
    return parts[parts?.length - 1] || '' // Get the last part
  }, [location])

  const history = useHistory()

  const userStatus = useAuth()

  const isLarge = useMatchMedia({ breakpoint: breakpoints.xl, prefix: 'max' })

  const { hasAnyWritePermission, hasAnyRequestPermission } = useMyAccountAccessQry()

  const hideMenu = useCallback(() => setOpened(false), [])

  const isLoggedIn = useMemo(() => userStatus === 'LOGGED_IN', [userStatus])

  const { data: unreadTickets } = useGetUnreadTicketsQuery(undefined, { pollingInterval: 30_000, skip: !isLoggedIn })
  const hasUnreadTickets = (unreadTickets?.length ?? 0) > 0

  const shouldHideButtons = useMemo(() => {
    return pathsToHidePaymentButtons(pathParam).some(element =>
      typeof element === 'string' ? element === pathName : element.test(pathName)
    )
  }, [pathName, pathParam])
  const shouldShowMobileButtons = useMemo(() => pathsToShowOnlyMobilePaymentButtons.includes(pathName), [pathName])
  const shouldIncreaseHeight = useMemo(() => pathsToIncreaseHeight.includes(pathName), [pathName])
  const isDashboard = useMemo(() => pathName === paths.dashboard(), [pathName])
  const shouldShowBottomBorder = useMemo(
    () => pathsToShowBottomBorder(pathParam).includes(pathName),
    [pathName, pathParam]
  )

  const onCloseHamburgerMenu = () => {
    setOpened(!opened)
  }

  useEffect(() => {
    if (!isLarge) {
      hideMenu()
    }
  }, [isLarge])

  useEffect(() => {
    const pathname = location.pathname

    const state =
      pathname !== paths.login() &&
      pathname !== paths.checkEmail() &&
      pathname !== paths.passwordReset() &&
      pathname !== paths.changePasswordEmail() &&
      pathname !== paths.handlePasswordReset() &&
      pathname !== paths.handlePasswordResetInvited() &&
      !pathname.startsWith('/signup') &&
      !pathname.startsWith('/transfer-funds') &&
      !pathname.startsWith('/invoice/') &&
      !paths.isAdsPath(pathname) &&
      !paths.isExpoPath(pathname)

    setVisible(state)
  }, [location])

  useEffect(() => {
    const body = document.body
    if (body) {
      if (opened) {
        window?.scrollTo(0, 0)
        body.classList.add('menu-opened')
      } else {
        body.classList.remove('menu-opened')
      }
    }
  }, [opened])

  return (
    <div
      className={cn('header-wrapper', {
        stepper: shouldIncreaseHeight,
        'bottom-border': shouldShowBottomBorder
      })}
      data-visible={visible}
    >
      <div className='header-main-content'>
        {opened && <div className='overlay' onClick={hideMenu} />}
        <div className={cn('header-container', { 'no-buttons': shouldHideButtons, 'increased-width': isDashboard })}>
          {!isLarge ? <CustomHeaderTitles /> : <KingdomBankLogo onClick={() => history.push(paths.dashboard())} />}

          <div className='header-right-container'>
            <div className='header-right-wrapper'>
              <div className='header-right-side'>
                {!shouldHideButtons &&
                  (hasAnyWritePermission || (hasTransactionApprovalFlow && hasAnyRequestPermission)) &&
                  (!isLarge ? (
                    <PaymentButtons rowHeight={NAVIGATION_ROW_HEIGHT} />
                  ) : (
                    <PaymentButtonsMobile isOpenDropDown={opened} rowHeight={NAVIGATION_ROW_HEIGHT} />
                  ))}

                <MMLanguageMenu />

                <NotificationsBell enabled={isLoggedIn} hideBadge={opened} />

                {!isLarge && <ProfileMenu />}

                {shouldShowMobileButtons && isLarge && (
                  <PaymentButtonsMobile isOpenDropDown={opened} rowHeight={NAVIGATION_ROW_HEIGHT} />
                )}

                {isLarge && isLoggedIn && (
                  <div
                    className={cn('header-container-inner', {
                      'increased-width': isDashboard
                    })}
                  >
                    <div>
                      <div
                        data-for='tooltip-hamburger-menu'
                        data-tip={tr('FRONTEND.HEADER.TOOLTIP.MENU', 'menu')}
                        className={cn('header-hamburger', { open: opened })}
                      >
                        <div className='header-hamburger-icon'>
                          <MMButton
                            type='button'
                            size='large'
                            variant='elevated'
                            collapsed
                            collapsedIcon={<div className='menu-icon' />}
                            onClick={() => setOpened(prevState => !prevState)}
                            className='header-hamburger-button'
                          />
                          {hasUnreadTickets && !opened && <span className='header-hamburger-unread-dot' />}
                        </div>
                      </div>
                      <HamburgerMenu opened={opened} onClose={onCloseHamburgerMenu} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isLarge && (
        <div className='header-onboarding-wrapper'>
          <OnboardingBanner />
          <CustomHeaderTitles />
        </div>
      )}
    </div>
  )
}
